import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function MembershipSetup({ title, message }) {
  const { user, refresh } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((p) => p || user?.company_id || r.data[0]?.id || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)).catch(() => {});
  }, [companyId]);

  const join = async () => {
    if (!companyId || !roleId) { toast.error("Pick a company and a role"); return; }
    setSaving(true);
    try {
      await api.post("/my/membership", { company_id: companyId, role_id: roleId });
      toast.success("Member profile set up — you can log your own entries now");
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="membership-setup">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="h-4 w-4 text-slate-400" />
          <div className="font-heading text-lg font-semibold">{title} — member setup</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">{message}</p>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
        <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setRoleId(""); }} data-testid="membership-company"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Your role</label>
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)} data-testid="membership-role"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          <option value="">Select…</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button onClick={join} disabled={saving} data-testid="membership-join"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Join as member"}
        </button>
      </div>
    </div>
  );
}
